/** @jsx isolateComponent */
import {
  Colors,
  Heading,
  HeadingLevel,
  Link,
  Typography,
} from '@zapier/design-system';
import { Icon } from '@zapier/design-system';
import { css } from '@emotion/react';

import { isolateComponent } from '@zapier/style-encapsulation';
import { useUniversalNavContext } from '../../../../hooks/useUniversalNavContext';
import { emitUserInteractionEvent } from '../../../../utils/avroEvents';
import { ArrowLinkIcon } from '../../ArrowLinkIcon';

import type { NavItemProduct2 } from '../../../../constants/links';
import type { IconName } from '@zapier/design-system';

const Styles = {
  root: css({
    display: 'flex',
    flexDirection: 'column',
    gap: 30,
    // Undoes UniversalTopbarLink style, which makes all text bold when toggle is hovered/focused
    fontWeight: 'initial',
    minHeight: 373,
  }),
  heading: css({
    ...Typography.pageHeader9,
    display: 'flex',
    flexDirection: 'column',
    color: Colors.earth,
    fontSize: '30px',
    lineHeight: '24px',
    letterSpacing: '1px',
  }),
  subheading: css({
    ...Typography.smallPrint1,
    color: Colors.neutral700,
    display: 'block',
    marginTop: 8,
  }),
  groupsContainer: css({
    display: 'flex',
    gap: '50px',
  }),
  groupHeading: css({
    ...Typography.sectionHeader3Semibold,
    // TODO: Does not meet minimum contrast ratio
    color: Colors.neutral500,
    fontSize: '14px',
    letterSpacing: '1px',
    marginBottom: '10px',
  }),
  linkList: css({
    display: 'flex',
    flexDirection: 'column',
    gap: '20px',
  }),
  linkContainer: css({
    textDecoration: 'none',
    display: 'flex',
    gap: '10px',
    alignItems: 'center',
    '&:hover [data-tag="link-heading"]': {
      color: Colors.zapier,
      textDecoration: 'none',
    },
    '&:hover [data-tag="link-description"]': {
      textDecoration: 'underline',
    },
    '&:hover [data-tag="arrow-icon-wrapper"]': {
      marginLeft: 4,
      opacity: 1,
    },
    '&:focus': {
      outline: `1px solid ${Colors.blue}`,
    },
  }),
  iconContainer: css({
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    width: 38,
    height: 38,
    borderRadius: '9px',
    backgroundColor: Colors.cream,
  }),
  linkHeading: css({
    ...Typography.paragraph3Medium,
    height: '26px',
    lineHeight: '30px',
    color: Colors.earth,
  }),
  linkDescription: css({
    ...Typography.smallPrint3,
    lineHeight: '20px',
    color: Colors.neutral700,
  }),
};

type MegaMenuProductLeftV2Props = NavItemProduct2['heroArea'] & {
  /**
   * Function called when closing menu on a link from within the MegaMenu
   */
  onCloseMenu?: () => void;
};

/**
 * @private
 * Wrapper for the content inside the V2 Product tab's left side
 */
export const MegaMenuProductLeftV2 = ({
  heading,
  onCloseMenu,
  subheading,
  submenu,
}: MegaMenuProductLeftV2Props) => {
  const { getFullyQualifiedURL } = useUniversalNavContext();

  const handleEvent = (text: string, eventType: string) => {
    emitUserInteractionEvent({
      event_action: eventType,
      option_selected: `Product tab Left: ${text}`,
    });
    if (eventType === 'click' && onCloseMenu) onCloseMenu();
  };

  return (
    <div css={Styles.root}>
      <header>
        <HeadingLevel>
          <Heading css={Styles.heading}>{heading}</Heading>
        </HeadingLevel>
        <span css={Styles.subheading}>{subheading}</span>
      </header>

      <div css={Styles.groupsContainer}>
        {submenu.map((group) => (
          <div key={group.text}>
            <HeadingLevel>
              <Heading css={Styles.groupHeading}>{group.text}</Heading>
            </HeadingLevel>
            <ul css={Styles.linkList}>
              {group.submenu?.map((link) => (
                <li key={link.text}>
                  <Link
                    color={null}
                    css={Styles.linkContainer}
                    href={getFullyQualifiedURL(link.href)}
                    target={link.target}
                    onClick={() => handleEvent(link.text, 'click')}
                    onMouseEnter={() => handleEvent(link.text, 'hover')}
                  >
                    {link.icon && (
                      <div css={Styles.iconContainer}>
                        <Icon
                          color="brand"
                          name={link.icon as IconName}
                          size={19}
                        />
                      </div>
                    )}
                    <div
                      css={{
                        display: 'flex',
                        flexDirection: 'column',
                      }}
                    >
                      <span css={Styles.linkHeading} data-tag="link-heading">
                        {link.text}
                      </span>
                      <span
                        css={Styles.linkDescription}
                        data-tag="link-description"
                      >
                        {link.description}
                        <ArrowLinkIcon />
                      </span>
                    </div>
                  </Link>
                </li>
              ))}
            </ul>
          </div>
        ))}
      </div>
    </div>
  );
};
